import './table-utils.scss';

import { PropertyFilterOperator } from '@amzn/awsui-collection-hooks';
import {
  Box,
  CollectionPreferencesProps,
  DatePicker,
  Icon,
  Input,
  MultiselectProps,
  Popover,
  PropertyFilterProps,
  Select,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import Badge from "@amzn/awsui-components-react/polaris/badge";
import Link from '@amzn/awsui-components-react/polaris/link';
import StatusIndicator, { StatusIndicatorProps } from '@amzn/awsui-components-react/polaris/status-indicator';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import { OrderItem, OrderStatusStamp, OrderSubLineItem } from '@amzn/ito-client';
import { ExpandableList } from 'common/components/ExpandableList/ExpandableList';
import CopyText from 'common/CopyText/CopyText';
import { getString } from 'common/ui-string-labels/ui-strings-utils';
import { ALPHANUMERIC_REGEXP, ALPHANUMERIC_WITH_DASH_REGEXP, buildAgeWithColor, buildRequestTypeBadge } from 'common/utils/form-utils';
import { ITMPV1_WEBSITE_URL, LINK_TO_COUPA,LINK_TO_HOP_ORDER, SYSTEM_FULFILL_NAME } from 'configuration/config';
import { regionsData, tableUtilsData } from "data";
import { ACTIONS, AMAZONIAN, ITSE_ROLE, PROCUREMENT, SUPPORT_MESSAGES_LABELS } from 'interfaces/constants';
import jp from 'jsonpath';
import { isArray } from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import { Link as LinkReactRouter } from 'react-router-dom';
import { FilesService } from 'services/files-service';

import CheckboxItem from '../../common/CheckboxItem/CheckboxItem';
import { RelatedOrdersLinks } from '../RelatedOrdersLinks/RelatedOrdersLinks';
import { ColumnSetting, VisibleOption } from './config-models';
import { buildDate, buildDateOnly, buildDateTimeLocal } from './time-utils';

export interface MappingData {
  statusMapping: Record<string, string>;
  tagsMapping: Record<string, string>;
}

export interface SortingState {
  sortBy?: string;
  sortOrder?: string;
}

/**
 * Props interface for configuring row editing in a table
 *
 * @property {OrderSubLineItem | null} editingItem - The row item currently being edited, representing a sub-line item in an order
 * @property {Record<string, any>} editedValues - Map of field keys to their current edited values, keyed by column ID or field name
 * @property {(values: Record<string, any>) => void} setEditedValues - Function to update the edited values for the row
 */
interface RowEditConfigProps {
  editingItem: OrderSubLineItem | null;
  editedValues: Record<string, any>;
  setEditedValues: (values: Record<string, any>) => void;
}

/**
 * Props interface for an editable table row component
 *
 * @extends RowEditConfigProps
 *
 * @property {any} item - The full data object for the row, including both editable and non-editable fields
 * @property {ColumnSetting} originalColumn - The original column definition for the editable cell or row, containing metadata such as column ID and header
 * @property {TableProps.EditConfig<any>} originalEditConfig - The table's original edit configuration to maintain consistent editing behavior
 */
export interface EditableRowProps extends RowEditConfigProps {
  item: any;
  originalColumn: ColumnSetting;
  originalEditConfig: TableProps.EditConfig<any>;
}

/**
 * Common action function callbacks for table row operations.
 *
 * @property {(item: any) => void} onEdit - Callback fired when the edit action is triggered for a row
 * @property {() => void} onCancel - Callback fired when the cancel action is triggered
 * @property {() => void} onSave - Callback fired when the save action is triggered
 */
export interface ActionFunctions {
  onEdit?: (item: any) => void;
  onCancel?: () => void;
  onSave?: () => void;
}

/**
 * Props interface for a table actions component, extending the common row action functions.
 *
 * @extends ActionFunctions
 *
 * @property {any} item - The full data object for the current row
 * @property {boolean} isEditing - Whether the current row is in editing mode
 * @property {boolean} hasChanges - Whether there are unsaved changes in the current row
 * @property {string} [savingRowId] - Optional ID of the row currently being saved; used to indicate loading state
 * @property {'byRow' | 'saveOnly'} [mode] - Action mode: byRow (Edit/Save/Cancel) or saveOnly (Save only)
 */
export interface TableActionsProps extends ActionFunctions {
  item: any;
  isEditing: boolean;
  hasChanges: boolean;
  savingRowId?: string;
  mode?: 'byRow' | 'saveOnly';
}

/**
 * Props interface for configuring table row actions, extending the common row action functions.
 * 
 * @extends ActionFunctions
 *
 * @property {string} [savingRowId] - Optional ID of the row currently being saved; used to indicate loading state
 * @property {React.ComponentType<TableActionsProps>} TableActions - A React component responsible for rendering action controls (Edit, Save, Cancel, etc.) for a table row
 * @property {'byRow' | 'alwaysEditable'} [editMode] - Editing mode: byRow (click to edit) or alwaysEditable (all fields editable by default)
 * @property {(item: any) => void} onEdit - Callback fired when the edit action is triggered for a row
 * @property {() => void} onCancel - Callback fired when the cancel action is triggered
 * @property {() => void | ((item: any) => void)} onSave - Callback fired when the save action is triggered
 * @property {(item: any) => void} [onSaveWithItem] - Alternative save callback that receives the item (for always-editable mode)
 */
export interface ActionConfigProps extends ActionFunctions {
  savingRowId?: string;
  TableActions: React.ComponentType<TableActionsProps>;
  editMode?: 'byRow' | 'alwaysEditable';
  onSaveWithItem?: (item: any) => void;
}

export const defaultPagingOptions = [
  {
    label: '30',
    value: 30
  },
  {
    label: '50',
    value: 50
  },
  {
    label: '70',
    value: 70
  }
];

const STANDARD = "standard";
const NOT_APPLICABLE = "N/A";
const EMPTY_STRING = "";
const DEFAULT_VISIBLE_ITEMS_COUNT = 5;


function getValueOrValues(item: any, selector: Array<any> | string) {
  let value: any = null;
  let values: string[] = [];
  if (isArray(selector)) {
    // Multiple values
    value = '';
    values = [];
    for (const eachSelector of selector) {
      const val = jp.value(item, eachSelector as string);
      value += ' ' + val;
      values.push(val as string);
    }
  } else {
    // Single value
    // Uses jsonpath to get the value
    value = jp.value(item, selector as string);
  }
  return { value, values };
}

export const validateRequiredField = (item: any, value: string | undefined): string | undefined => {
  if (!value || value.trim() === "") {
    return "This field is required.";
  }
  return undefined;
};

/**
 * Helper function to render an editable row
 * 
 * @param {any} item - The data item for the row
 * @param {RowEditConfigProps} rowEditConfig - Configuration for row editing
 * @param {ColumnSetting} column - The column definition
 * @param {TableProps.EditConfig<any>} editConfig - The edit configuration for the cell
 * @returns React component for the editable row
 */
export function renderEditableRow(
  item: any,
  rowEditConfig: RowEditConfigProps,
  column: ColumnSetting,
  editConfig: TableProps.EditConfig<any>
): React.ReactNode {
  return (
    <EditableRow
      item={item}
      editingItem={rowEditConfig.editingItem}
      editedValues={rowEditConfig.editedValues}
      setEditedValues={rowEditConfig.setEditedValues}
      originalColumn={column}
      originalEditConfig={editConfig}
    />
  );
}

export function buildColumnDefinitions(
  columnSettings: ColumnSetting[],
  onlyThese?: readonly string[],
  editEnabled?: boolean,
  rowEditConfig?: RowEditConfigProps
): TableProps.ColumnDefinition<any>[] {
  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [];
  for (const column of columnSettings) {
    const showIt: boolean = checkIfShowIt(column, onlyThese);
    if (!showIt) {
      continue;
    }
    const cellFunction = (item: any): React.ReactNode => {
      // If row editing is enabled and this row is being edited
      if (rowEditConfig &&
        rowEditConfig.editingItem &&
        item.id === rowEditConfig.editingItem.id &&
        column.editType &&
        column.id !== ACTIONS.toLowerCase()) {
        return renderEditableRow(
          item,
          rowEditConfig,
          column,
          {
            editingCell: editFunction,
            validation: column.editConfig?.validation === "validateRequiredField" ? validateRequiredField : undefined,
          }
        );
      }

      let id: any = null;
      let url = "";
      const { value, values } = getValueOrValues(item, column.selector);

      // Set display based on field type...
      switch (column.type) {
        case 'date':
          return buildDate(value);
        case 'date_only':
          return buildDateOnly(value);
        case 'date_utc':
          if (value) {
            const dateUtc = moment(value as string)
              .utc()
              .format('MM/DD/YYYY');
            return <span title={value}>{dateUtc}</span>;
          }
          break;

        case 'datetime_utc':
          if (value) {
            const local = moment(value as string)
              .utc()
              .format('MM/DD/YYYY HH:mm:ss');
            return <span title={value}>{local}</span>;
          }
          break;

        case 'datetime_local':
          return buildDateTimeLocal(value);

        case 'assignee_alias':
          return buildAssigneeAlias(value as string);

        case 'requester_info':
          return buildRequesterInfo(values);

        case 'requestedBy_info':
          return buildRequestedByInfo(value);

        case 'selectme':
          return <CheckboxItem key={value} name="orderId" id={id}></CheckboxItem>;

        case 'image_url':
          return buildImageUrl(value as string, column.props);

        case 'link':
          if (column.linkSetting) {
            id = encodeURIComponent(jp.value(item, column.linkSetting.idSelector as string));
            url = column.linkSetting.url.replace('{id}', id);
          }
          return (
            <LinkReactRouter target="_blank" to={url} state={{ id, value }}>
              {value}
            </LinkReactRouter>
          );

        case 'tag':
          return buildOrderTags(value);

        case 'external_link':
          return (
            <Link
              external
              href={value}
              variant="secondary"
            >
              {value}
            </Link>
          );

        case 'status':
          return buildStatusLabel(value);

        case 'hop-link':
          return buildHopLink(value);

        case 'po-number-link':
          return buildPoNumberLink(value);

        case 'lineStatusLabel':
          return buildLineStatusLabel(value);

        case 'categories':
          if (value) {
            return value.join(', ');
          }
          break;

        case 'request_category':
          return buildRequestCategory(values);
        case 'last_updated_date':
          if (value) {
            return buildLastUpdate(value);
          }
          break;

        case 'request_type':
          return buildRequestTypeBadge(values);

        case "build-ship-to":
          return buildShipTo(values);

        case "lineItemDeliveredQuantityCalculation":
          return buildLineItemQuantityDeliveredCalculation(value);

        case "lineItemRemainingQuantityCalculation":
          return buildLineItemQuantityRemainingCalculation(values);

        case "ageWithColor":
          return buildAgeWithColor(value);
        case "legacy_order_link":
          return buildLinkToLegacyOrder(values);

        case "related_orders_link":
          return buildLinkToRelatedOrder(values);

        case "pr_number_list":
          return buildSublineItemFieldList(value, "prNumber", "No PR numbers available");

        case "po_number_list":
          return buildSublineItemFieldList(value, "poNumber", "No PO numbers available");

        case "splitPanelSKU":
          return buildSplitPanelSKU(values);

        case "bulkDownloadOriginalFile":
          return buildBulkDownloadOriginalFile(value);

        case "bulkDownloadResultsFile":
          return buildBulkDownloadResultsFile(value);

        // Calculate the count of all items from the order
        case 'total_quantity':
          if (value) {
            const total = value.reduce((total: number, item: OrderItem) => total + (item.quantity ?? 0), 0);

            return <span title={total}>{total}</span>;
          }
          break;

        case 'requested_items':
          return buildItemsRequested(value);


        case "supportMessageIndicator":
          return buildSupportMessageIndicator(values);

        default:
          return <span>{value}</span>;
      }
    };

    const editFunction = (item: any, props: TableProps.CellContext<any>): React.ReactNode => {
      if (!column.editType) {
        return undefined;
      }

      const { value, values } = getValueOrValues(item, column.selector);

      // Initialize currentValue with the existing value if it's undefined and if it is one of the required fields
      if (props.currentValue === undefined && (column.id === "vendor" || column.id === "prNumber")) {
        props.setValue(value);
      }

      switch (column.editType) {
        case "allCharactersInput":
          return buildInput(value, props.currentValue, props.setValue);
        case "alphanumericInput":
          return buildInput(value, props.currentValue, props.setValue, ALPHANUMERIC_REGEXP);
        case "alphanumericWithDashInput":
          return buildInput(value, props.currentValue, props.setValue, ALPHANUMERIC_WITH_DASH_REGEXP);
        case "numericInput":
          return buildNumericInput(value, props.currentValue, props.setValue);
        case "itemsStatusSelect":
          return buildSelectForLineItemsStatus(value, props.currentValue, props.setValue);
        case "datePicker":
          return buildDatePicker(value, props.currentValue, props.setValue);
        default:
          return undefined;
      }
    };

    columnDefinitions.push({
      ...column,
      cell: cellFunction,
      editConfig: editEnabled && column.editType ? {
        editingCell: editFunction,
        validation: column.editConfig?.validation === "validateRequiredField" ? validateRequiredField : undefined,
      } : undefined,
      sortingField: column.sortable ? (column.sortingField ?? column.id) : undefined
    });
  }
  return columnDefinitions;
}

// User Alias
function buildRequesterInfo(values: string[]): React.ReactNode {
  const userAlias = values[0] ?? "alias unavailable";
  const userEmail = values[1] ?? "email unavailable";

  return (
    <SpaceBetween size='xs'>
      <Link external href={`https://maple-admin-prod.corp.amazon.com/my-support/person/${userAlias}`} target={"_blank"}>@{userAlias}</Link>
      <CopyText textContent={userEmail} copyContent={userEmail} isHtml={false} />
    </SpaceBetween>
  );
}

function buildRequestedByInfo(value: string | undefined): React.ReactNode {
  const userAlias = value ?? "alias unavailable";

  return (
    <Link external href={`https://maple-admin-prod.corp.amazon.com/my-support/person/${userAlias}`} target={"_blank"}>@{userAlias}</Link>
  );
}

// Assignee Alias
function buildAssigneeAlias(value?: string): React.ReactNode {
  if (value) {
    return <Box variant={'p'}> {value !== SYSTEM_FULFILL_NAME ? `@${value}` : value} </Box>;
  }

  return (
    <Box color={'text-body-secondary'} variant={'p'}>
      Unassigned
    </Box>
  );
}

type Color = "green" | "blue" | "red" | "grey";
type TagKeys = 'ITSE' | 'AMAZONIAN' | 'FTE';

const BADGE_COLORS = {
  RED: "red" as Color,
  BLUE: "blue" as Color,
  GREEN: "green" as Color,
  GREY: "grey" as Color,
};

// Initialize TAGS_MAPPING with known mappings
const TAGS_MAPPING: Record<TagKeys, Color> = Object.keys(tableUtilsData.table_utils.tagsMapping).reduce((acc, key) => {
  acc[key as TagKeys] = tableUtilsData.table_utils.tagsMapping[key as TagKeys] as Color;
  return acc;
}, {} as Record<TagKeys, Color>);

// Build a badge for a given label value, defaulting to grey for unspecified tags
export function buildTag(value: string): React.ReactElement {
  const tagColor = TAGS_MAPPING[value as TagKeys] || 'grey';
  return (
    <Box key={value} margin={{ bottom: "xxs" }}>
      <Badge color={tagColor}>{value}</Badge>
    </Box>
  )
}

// Function to render all tags for the orderLabels field in a single cell
export function buildOrderTags(orderLabels?: { label: string; description: string; on: string; via: string }[]): React.ReactElement {
  if (!orderLabels) {
    return <div />;
  }

  return (
    <div>
      {orderLabels.map(orderLabel => buildTag(orderLabel.label))}
    </div>
  );
}


// Image
interface ImageProps {
  width: string;
}
function buildImageUrl(theUrl: string, props: ImageProps): React.ReactNode {
  return <img src={theUrl} width={props.width} alt="" />;
}

///////////////////
export function buildFilteringProperties(columnSettings: ColumnSetting[]): PropertyFilterProps.FilteringProperty[] {
  const filters: PropertyFilterProps.FilteringProperty[] = [];
  for (const column of columnSettings) {
    if (!column.operators && !column.defaultOperator) continue;
    filters.push({
      key: column.id,
      propertyLabel: column.header,
      operators: column.operators ? (column.operators as PropertyFilterOperator[]) : undefined,
      defaultOperator: column.defaultOperator ? (column.defaultOperator as PropertyFilterOperator) : undefined,
      groupValuesLabel: column.header
    });
  }
  return filters;
}

/**
 * Prepares the options for the preferences, to set visible columns
 * @param columnSettings The settings from the json file
 * @returns The array of options for visible content
 */
export function buildVisibleContentOptions(
  columnSettings: ColumnSetting[]
): CollectionPreferencesProps.VisibleContentOption[] {
  const options: CollectionPreferencesProps.VisibleContentOption[] = [];
  for (const column of columnSettings) {
    if (column.visible === VisibleOption.never) continue;

    options.push({
      id: column.id,
      label: column.header,
      editable: column.visible !== VisibleOption.always
    });
  }
  return options;
}

/**
 * Builds the options for contentDisplayPreference from column settings.
 * - Columns with visible "never" are excluded entirely (internal filter fields).
 * - Columns with visible "always" are listed first with alwaysVisible: true so the
 * user can reorder them but cannot hide them, and they always appear at the top by default.
 * - All other columns follow in their original order with alwaysVisible: false.
 * @param columnSettings The settings from the json file
 * @returns The array of content display options
 */
export function buildContentDisplayOptions(
  columnSettings: ColumnSetting[]
): CollectionPreferencesProps.ContentDisplayOption[] {
  const filtered = columnSettings.filter(col => col.visible !== VisibleOption.never);
  const alwaysCols = filtered.filter(col => col.visible === VisibleOption.always);
  const otherCols = filtered.filter(col => col.visible !== VisibleOption.always);

  return [...alwaysCols, ...otherCols].map(col => ({
    id: col.id,
    label: col.header,
    alwaysVisible: col.visible === VisibleOption.always
  }));
}

/**
 * Merges saved contentDisplay preferences with the currently active columns.
 *
 * Rules:
 * - Only items whose IDs exist in activeColumns are kept (filters out columns
 *   that belong to other tabs, e.g. support-only columns).
 * - Columns with visible "always" are forced to visible: true and prepended
 *   if they are missing from the saved preferences (e.g. user navigated from
 *   another tab that didn't have them).
 * - The saved order of all other items is preserved.
 *
 * @param activeColumns The columns currently visible for the active tab
 * @param savedContentDisplay The contentDisplay array stored in preferences
 * @returns A merged, tab-scoped contentDisplay array ready to pass to CollectionPreferences
 */
export function buildActiveContentDisplay(
  activeColumns: ColumnSetting[],
  savedContentDisplay: ReadonlyArray<CollectionPreferencesProps.ContentDisplayItem> | undefined
): CollectionPreferencesProps.ContentDisplayItem[] {
  const activeIds = new Set(
    activeColumns
      .filter(col => col.visible !== VisibleOption.never)
      .map(col => col.id)
  );
  const alwaysColumns = activeColumns.filter(col => col.visible === VisibleOption.always);
  const alwaysIds = new Set(alwaysColumns.map(col => col.id));

  const savedItems = (savedContentDisplay ?? [])
    .filter(item => activeIds.has(item.id))
    .map(item => alwaysIds.has(item.id) ? { ...item, visible: true } : item);

  const savedIds = new Set(savedItems.map(item => item.id));
  const missingAlwaysItems = alwaysColumns
    .filter(col => !savedIds.has(col.id))
    .map(col => ({ id: col.id, visible: true }));

  // Append columns added to the config after the user last saved preferences.
  const missingOtherItems = activeColumns
    .filter(col => col.visible !== VisibleOption.never
      && col.visible !== VisibleOption.always
      && !savedIds.has(col.id))
    .map(col => ({ id: col.id, visible: col.visible === VisibleOption.yes }));

  return [...missingAlwaysItems, ...savedItems, ...missingOtherItems];
}

/**
 * Builds column definitions ordered by contentDisplay preference.
 * @param columnSettings The settings from the json file
 * @param contentDisplay The ordered content display items from preferences
 * @returns Column definitions in the order specified by contentDisplay
 */
export function buildOrderedColumnDefinitions(
  columnSettings: ColumnSetting[],
  contentDisplay?: ReadonlyArray<CollectionPreferencesProps.ContentDisplayItem>,
  editEnabled?: boolean,
  rowEditConfig?: RowEditConfigProps
): TableProps.ColumnDefinition<any>[] {
  if (!contentDisplay) {
    return buildColumnDefinitions(columnSettings, undefined, editEnabled, rowEditConfig);
  }

  const visibleIds = contentDisplay.filter(item => item.visible).map(item => item.id);
  const alwaysVisible = columnSettings
    .filter(col => col.visible === VisibleOption.always && !visibleIds.includes(col.id));

  const orderedIds = [...alwaysVisible.map(col => col.id), ...visibleIds];

  const allDefs = buildColumnDefinitions(columnSettings, orderedIds, editEnabled, rowEditConfig);

  const orderMap = new Map(orderedIds.map((id, idx) => [id, idx]));
  allDefs.sort((a, b) => (orderMap.get(a.id as string) ?? 999) - (orderMap.get(b.id as string) ?? 999));

  return allDefs;
}

/**
 * Returns Cloudscape Select Options from an Array of strings
 * @param array The list of strings you want as options
 * @returns The options for the Select
 */
export function buildSelectOptionsFromStrings(array: string[]) {
  const selectOptions: SelectProps.Option[] = array.map(stringValue => {
    return { label: stringValue, value: stringValue } as SelectProps.Option;
  });
  return selectOptions;
}

/**
 * Maps the regions.json data into MultiSelect option groups
 * @returns Countries grouped by regions to use in the MultiSelect Cloudscape Component
 */
export function buildMultiSelectLocationOptions() {
  const selectOptions: MultiselectProps.OptionGroup[] = regionsData.map(region => {
    const countries = region.countries.map(country => {
      return { id: country, label: country, value: country } as MultiselectProps.Option;
    });
    return { label: region.region, options: countries } as MultiselectProps.OptionGroup;
  });
  return selectOptions;
}

function buildLineItemQuantityDeliveredCalculation(value: OrderSubLineItem[]) {
  if (!value) {
    return "-";
  }

  let total = 0;
  value.map((sublineItem) => total += sublineItem.quantityDelivered ?? 0);
  return total;
}

function buildLineItemQuantityRemainingCalculation(values: any[]) {
  const sublineItems = values[0] as OrderSubLineItem[];
  const totalQuantityRequested = values[1] as number;

  if (!sublineItems || !totalQuantityRequested) {
    return "-";
  }

  let totalDelivered = 0;
  sublineItems.map((sublineItem) => totalDelivered += sublineItem.quantityDelivered ?? 0);

  const remainingQuantity = totalQuantityRequested - totalDelivered;
  return remainingQuantity;
}

function buildSublineItemFieldList(value: any[], key: keyof OrderSubLineItem, isUndefinedMessage: string): string {
  const sublineItems = value as OrderSubLineItem[];

  if (!sublineItems) {
    return isUndefinedMessage;
  }

  const listOfNumbers: string[] = sublineItems
    .map((sublineItem) => sublineItem[key]?.toString() ?? "")
    .filter((number) => number.trim() !== ""); // Filter out empty strings

  return listOfNumbers.join(', ');
}

function buildSplitPanelSKU(values: any[]) {
  // Comes from .itemInformation.sku
  const standardItemSKU = values[0];

  // Comes from .details.sku
  const nonStandardItemSKU = values[1];

  let skuToDisplay = nonStandardItemSKU ?? standardItemSKU;
  return <span>{skuToDisplay}</span>;
}

// Inline editing
export function buildInput(value: any, formValue: any, setFormValue: (value: any) => void, regexpValidation?: RegExp) {
  return (
    <Input
      value={formValue === undefined ? value : formValue}
      onChange={({ detail }) => {
        if (!regexpValidation || regexpValidation.test(detail.value)) {
          setFormValue(detail.value)
        }
      }}
    />
  );
}

export function buildNumericInput(value: any, formValue: any, setFormValue: (value: any) => void) {
  const MAX_32BIT_INT = 2147483646;
  const inputValue = parseInt(formValue === undefined ? value : formValue, 10) || 0;
  const isOverLimit = inputValue > MAX_32BIT_INT;

  return (
    <Input
      type="number"
      value={formValue === undefined ? value : formValue}
      invalid={isOverLimit}
      onChange={({ detail }) => {
        const value = detail.value === '' ? 0 : parseInt(detail.value, 10);
        if (value >= 0 && value <= MAX_32BIT_INT) {
          setFormValue(value)
        }
      }}
    />
  );
}

export function buildDatePicker(value: any, formValue: any, setFormValue: (value: any) => void) {
  const dateValue = formValue ?? value;
  return (
    <DatePicker
      expandToViewport
      onChange={({ detail }) => {
        if (detail.value && detail.value.trim() !== '') {
          const momentDate = moment(detail.value);
          if (momentDate.isValid()) {
            setFormValue(momentDate.toDate());
          }
        } else {
          setFormValue(null);
        }
      }}
      value={dateValue && dateValue instanceof Date ? moment(dateValue.toISOString()).format("YYYY-MM-DD") : ""}
      placeholder="YYYY/MM/DD"
    />
  )
}

export function buildSelectForLineItemsStatus(value: any, formValue: any, setFormValue: (value: any) => void) {
  // Replace "_" with whitespace and capitalize first words
  const getDisplayName = (word: string) =>
    word
      .replace( // Replace "_"
        /_/g, " "
      )
      .replace( // Capitalize
        /\w+/g, (w: string) => w[0].toUpperCase() + w.slice(1).toLowerCase()
      );

  const statusMappingSelect = getOrderLineItemStatusSelectMapping();
  const options: SelectProps.Option[] =
    Object.keys(statusMappingSelect).map((status) => {
      return {
        label: getDisplayName(status),
        value: status,
        iconName: statusMappingSelect[status]
      } as SelectProps.Option
    });

  return (
    <Select
      data-cy="line-status-dropdown"
      data-testid="line-status-dropdown"
      expandToViewport={true}
      selectedOption={{ label: getDisplayName(formValue ?? value), value: formValue ?? value }}
      options={options}
      onChange={(event) => setFormValue(event.detail.selectedOption.value)}
    />
  )
}

function checkIfShowIt(column: ColumnSetting, onlyThese: readonly string[] | undefined): boolean {
  if (column.visible === VisibleOption.never) {
    return false;
  }
  if (column.visible === VisibleOption.always) {
    return true;
  }
  if (!onlyThese) {
    if (column.visible === VisibleOption.no) {
      // Hidden by default
      return false;
    }

    // Visible by default
    return true;
  } else {
    // Check based on selected preferences
    return onlyThese.indexOf(column.id) >= 0;
  }
}

// Status
export function buildStatusLabel(status: string | undefined) {
  const orderStatusMapping: { [key: string]: string } = tableUtilsData.table_utils.statusMapping;
  return buildStatusIndicator(status, orderStatusMapping);
}

export function buildLineStatusLabel(status: string | undefined) {
  const lineStatusMapping: { [key: string]: string } = tableUtilsData.table_utils.lineItemStatusMapping;
  return buildStatusIndicator(status, lineStatusMapping);
}

function buildStatusIndicator(status: string | undefined, mapping: { [key: string]: string }) {
  const lowerCaseStatus = status ? (status as string).toLowerCase().replace(/_/g, " ") : "Unable to retrieve status";
  const statusType = status ? mapping[status] as StatusIndicatorProps.Type : undefined;
  return (
    <span className="status">
      <StatusIndicator type={statusType ?? "error"}>{lowerCaseStatus}</StatusIndicator>
    </span>
  );
}

function buildLinkToLegacyOrder(values: any) {
  const legacyId = values[0];

  if (!legacyId || legacyId.length === 0) {
    return <>{NOT_APPLICABLE}</>;
  }

  const firstEdit = values[1];
  const legacyLink = `${ITMPV1_WEBSITE_URL}procurement/hardware_orders/${legacyId}/edit`;
  const isBlocked = firstEdit && firstEdit !== "ham";
  return (
    <SpaceBetween size="xs" direction="horizontal">
      <Link external href={legacyLink}>{legacyId}</Link>
      {isBlocked &&
        <Popover dismissButton={false} position="top" content={getString("common.readOnlyMessage")}>
          <StatusIndicator type="stopped" colorOverride="yellow" />
        </Popover>}
    </SpaceBetween>
  )
};

function buildLinkToRelatedOrder(values: any) {
  return <RelatedOrdersLinks values={values} />;
};

function buildLastUpdate(value: OrderStatusStamp[]): React.ReactNode {
  const validEntries = value.length != 0 ? value.filter(entry => entry.on !== undefined) : [];

  if (validEntries.length === 0) {
    return (<div></div>);
  }

  const lastUpdateDate = validEntries.reduce((latest, entry) => {
    return entry.on && latest.on && new Date(entry.on) > new Date(latest.on) ? entry : latest;
  }).on;

  return buildDateOnly(lastUpdateDate);
}

function buildBulkDownloadOriginalFile(
  value: string
): React.ReactNode {

  return (<SpaceBetween direction="horizontal" size="xs">
    <Icon name="download" variant="link" />
    <Link
      data-testid="download-file-original"
      href="#"
      onFollow={() => FilesService.instance.downloadBulkImportFile(encodeURI(value), "original")}
    >
      {value}
    </Link>
  </SpaceBetween>
  );
}

function buildBulkDownloadResultsFile(
  value: string
): React.ReactNode {

  if (value)
    return (<SpaceBetween direction="horizontal" size="xs">
      <Icon name="download" variant="link" />
      <Link
        data-testid="download-file-results"
        href="#"
        onFollow={() => FilesService.instance.downloadBulkImportFile(encodeURI(value.replace("results/", "")), "results")}
      >
        {getString("imports.download")}
      </Link>
    </SpaceBetween>
    );

  return (<SpaceBetween direction="horizontal" size="xs">
    <Icon name="status-positive" variant='success' />
    {getString("imports.allRowsProcessed")}
  </SpaceBetween>);
}

export function buildHopLink(value: string | undefined): React.ReactNode {
  if (!value) {
    return <Box color="text-body-secondary" variant="p">{NOT_APPLICABLE}</Box>;
  }
  const VALUE_TO_REPLACE = "{HOP_ORDER_ID}"
  const builtURL = LINK_TO_HOP_ORDER.replace(VALUE_TO_REPLACE, value);

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Link
        external
        data-testid="hop-order-link"
        href={builtURL}
      >
        {value}
      </Link>
    </SpaceBetween>
  );
}

export function buildPoNumberLink(id: string | undefined): React.ReactNode {
  if (!id) {
    return <Box color="text-body-secondary" variant="p">{NOT_APPLICABLE}</Box>;
  }

  let value = id;
  if (id.includes("-")) {
    value = id.split("-")[1];
  }
  const VALUE_TO_REPLACE = "{PO_NUMBER}";
  const builtURL = LINK_TO_COUPA.replace(VALUE_TO_REPLACE, value);

  return (
    <SpaceBetween direction="horizontal" size="xs">
      <Link
        external
        data-testid="poNumberLink"
        href={builtURL}
      >
        {id}
      </Link>
    </SpaceBetween>
  );
}

export function buildShipTo(values: any[]): React.ReactNode {
  const [building, deliveryTo] = values;

  if (building) {
    return <span>{building}</span>;
  }

  if (deliveryTo && typeof deliveryTo === 'object') {
    const {
      addressLineData = [],
      city = EMPTY_STRING,
      citySubdivision1 = EMPTY_STRING,
      country = EMPTY_STRING
    } = deliveryTo;

    const line = addressLineData[0] ?? EMPTY_STRING;
    const parts = [line, city, citySubdivision1, country].filter(Boolean);

    return <span>{parts.join(", ")}</span>;
  }

  return <span>-</span>;
}

export function buildRequestCategory(values: any[]): React.ReactNode {
  const [requestCategories, amazonianSubCategory] = values;

  if (Array.isArray(requestCategories) && requestCategories.length > 0) {
    return requestCategories.join(', ');
  }

  if (amazonianSubCategory) {
    return <span>{amazonianSubCategory}</span>;
  }

  return <span>-</span>;
}

export function getStatusSelectMapping() {
  const statusMappingForSelect: { [key: string]: string } = tableUtilsData.table_utils.statusSelectMapping;
  return statusMappingForSelect;
}

export function getOrderLineItemStatusSelectMapping() {
  const statusMappingForSelect: { [key: string]: string } = tableUtilsData.table_utils.lineItemSelectStatusMapping;
  return statusMappingForSelect;
}

/**
 * EditableRow component
 *
 * Renders an editable table row cell when the given row is in editing mode:
 * It determines the field being edited based on the column's selector or ID,
 * retrieves the current value from either the edited values map or the item itself,
 * and passes the editing context to the table's original `editingCell` renderer.
 *
 * @component
 *
 * @param {any} item - The full data object for the row
 * @param {OrderSubLineItem} editingItem - The row item currently being edited
 * @param {Record<string, any>} editedValues - Map of field keys to their current edited values
 * @param {(values: Record<string, any>) => void} setEditedValues - Function to update the edited values for the row
 * @param {ColumnSetting} originalColumn - The original column definition for the editable cell or row
 * @param {TableProps.EditConfig<any>} originalEditConfig - The table's original edit configuration
 *
 * @returns {React.ReactNode} The rendered editable cell if the row is in edit mode, otherwise `null`
 */
export const EditableRow: React.FunctionComponent<EditableRowProps> = ({
  item,
  editingItem,
  editedValues,
  setEditedValues,
  originalColumn,
  originalEditConfig
}) => {
  if (editingItem && item.id === editingItem.id && originalEditConfig) {
    let fieldName: string;

    // Handle both string and array selectors
    if (typeof originalColumn.selector === 'string') {
      fieldName = originalColumn.selector.slice(2) as string;
    } else if (Array.isArray(originalColumn.selector) && originalColumn.selector.length > 0) {
      fieldName = (originalColumn.selector[0] as string).slice(2) as string;
    } else {
      fieldName = originalColumn.id;
    }

    const currentValue = editedValues[fieldName] !== undefined ?
      editedValues[fieldName] :
      (item[fieldName] as any);

    const cellContext = {
      currentValue,
      setValue: (value: any) => {
        setEditedValues({
          ...editedValues,
          [fieldName]: value
        });
      },
      submitValue: () => { }
    };

    // Render the editing cell using the original editConfig
    return originalEditConfig.editingCell(item, cellContext);
  }

  return null;
};



/**
 * Configuration interface for always-editable table rows
 * 
 * @interface AlwaysEditableRowConfig
 * @property {Record<string, Record<string, any>>} editedValues - Map of row IDs to their edited field values
 * @property {(itemId: string, fieldName: string, value: any) => void} handleFieldChange - Callback to handle field value changes
 */
interface AlwaysEditableRowConfig {
  editedValues: Record<string, Record<string, any>>;
  handleFieldChange: (itemId: string, fieldName: string, value: any) => void;
  autoStatusChangedIds?: Set<string>;
}

/**
 * Enhances column definitions to support row-level editing
 * 
 * @param {ColumnSetting[]} columns - Original column settings
 * @param {TableProps.ColumnDefinition<any>[]} baseColumnDefs - Base column definitions
 * @param {RowEditConfigProps | AlwaysEditableRowConfig} rowEditConfig - Configuration for row editing
 * @param actionConfig - Configuration for action buttons
 * 
 * @returns Enhanced column definitions with row editing support
 */
export function enhanceColumnDefinitionsForRowEditing(
  columns: ColumnSetting[],
  baseColumnDefs: TableProps.ColumnDefinition<any>[],
  rowEditConfig: RowEditConfigProps | AlwaysEditableRowConfig,
  actionConfig: ActionConfigProps
): TableProps.ColumnDefinition<any>[] {
  const editMode = actionConfig.editMode || 'byRow';
  let columnDefs = [...baseColumnDefs];

  // Add an actions column
  columnDefs.push({
    id: ACTIONS.toLowerCase(),
    header: ACTIONS,
    cell: (item: any) => {
      let isEditing: boolean;
      let hasChanges: boolean;

      if (editMode === 'alwaysEditable') {
        const alwaysEditConfig = rowEditConfig as AlwaysEditableRowConfig;
        isEditing = true;
        hasChanges = Object.keys(alwaysEditConfig.editedValues[item.id] || {}).length > 0;
      } else {
        const byRowConfig = rowEditConfig as RowEditConfigProps;
        isEditing = Boolean(byRowConfig.editingItem && byRowConfig.editingItem.id === item.id);
        hasChanges = Object.keys(byRowConfig.editedValues).length > 0;
      }

      return (
        <actionConfig.TableActions
          item={item}
          isEditing={isEditing}
          hasChanges={hasChanges}
          savingRowId={actionConfig.savingRowId}
          onEdit={actionConfig.onEdit}
          onCancel={actionConfig.onCancel}
          onSave={editMode === 'alwaysEditable' && actionConfig.onSaveWithItem
            ? () => actionConfig.onSaveWithItem!(item)
            : actionConfig.onSave}
          mode={editMode === 'alwaysEditable' ? 'saveOnly' : 'byRow'}
        />
      );
    },
    minWidth: editMode === 'alwaysEditable' ? 150 : 200
  });

  // Customize each column definition based on edit mode
  columnDefs = columnDefs.map(column => {
    if (column.id === ACTIONS.toLowerCase()) {
      return column;
    }

    const originalCell = column.cell;
    const originalEditConfig = column.editConfig;
    const originalColumn = columns.find(c => c.id === column.id);

    if (!originalColumn || !originalEditConfig) {
      return column;
    }

    return {
      ...column,
      cell: (item: any) => {
        if (editMode === 'alwaysEditable' && originalColumn.editType) {
          return renderAlwaysEditableCell(
            item,
            originalColumn,
            rowEditConfig as AlwaysEditableRowConfig
          );
        } else if (editMode === 'byRow') {
          const byRowConfig = rowEditConfig as RowEditConfigProps;
          if (byRowConfig.editingItem && item.id === byRowConfig.editingItem.id && originalEditConfig) {
            return renderEditableRow(
              item,
              byRowConfig,
              originalColumn,
              originalEditConfig
            );
          }
        }

        return originalCell(item);
      },
      editConfig: editMode === 'byRow' ? originalEditConfig : undefined
    };
  });

  return columnDefs;
}

/**
 * Wrapper that briefly highlights a cell when its value was auto-changed.
 * Re-keyed on each auto-change so the CSS animation replays automatically.
 */
const AutoStatusHighlight: React.FC<{ active: boolean; children: React.ReactNode }> = ({ active, children }) => {
  if (!active) return <>{children}</>;

  return (
    <div data-testid="auto-status-highlight" style={{ position: 'relative' }}>
      <div className="auto-status-highlight">
        {children}
      </div>
      <span className="auto-status-label">
        <span><Icon name="status-info"/></span>
        Auto-updated
      </span>
    </div>
  );
};

/**
 * Renders an editable cell for tables in always-editable mode
 * 
 * @param {any} item - The data item for the current row
 * @param {ColumnSetting} column - The column configuration containing edit settings
 * @param {AlwaysEditableRowConfig} config - Configuration for handling editable state
 * @returns {React.ReactNode} The rendered editable cell component
 */
function renderAlwaysEditableCell(
  item: any,
  column: ColumnSetting,
  config: AlwaysEditableRowConfig
): React.ReactNode {
  let fieldName: string;
  if (typeof column.selector === 'string') {
    fieldName = column.selector.slice(2);
  } else if (Array.isArray(column.selector) && column.selector.length > 0) {
    fieldName = (column.selector[0] as string).slice(2);
  } else {
    fieldName = column.id;
  }

  // Get the original value using the column selector to properly handle nested paths
  const { value: originalValue } = getValueOrValues(item, column.selector);

  const itemId = item.id || '';
  const itemEditedValues = config.editedValues[itemId] || {};
  const currentValue = itemEditedValues[fieldName] !== undefined
    ? itemEditedValues[fieldName]
    : originalValue;

  const setValue = (value: any) => config.handleFieldChange(item.id, fieldName, value);

  switch (column.editType) {
    case "alphanumericInput":
      return buildInput(originalValue, currentValue, setValue, ALPHANUMERIC_REGEXP);
    case "alphanumericWithDashInput":
      return buildInput(originalValue, currentValue, setValue, ALPHANUMERIC_WITH_DASH_REGEXP);
    case "numericInput":
      return buildNumericInput(originalValue, currentValue, setValue);
    case "itemsStatusSelect": {
      const isAutoChanged = !!(config.autoStatusChangedIds?.has(itemId));
      return (
        <AutoStatusHighlight active={isAutoChanged}>
          {buildSelectForLineItemsStatus(originalValue, currentValue, setValue)}
        </AutoStatusHighlight>
      );
    }
    case "datePicker":
      return buildDatePicker(originalValue, currentValue, setValue);
    default:
      // allCharactersInput
      return buildInput(originalValue, currentValue, setValue);
  }
}

export type { AlwaysEditableRowConfig };

/**
 * Given an array of values [status, role, …], returns a
 * <Badge> whose color and text reflect the support‐message state:
 * - Red + “Pending” when the order is open and the last
 *    message was from an Amazonian.
 * - Blue + “Up to date” when the order is open and the last message
 *    was from Procurement.
 * - Green + “Closed” when the order is in a non-open (complete) state.
 * - Grey + “N/A” for any other case.
 *
 * @param values Array of values where:
 *  - values[0]: the current order status (HardwareOrderStatus)
 *  - values[1]: the role of the last commenter ("amazonian"|"procurement"|…)
 * @returns A styled <Badge> component with the appropriate color and label.
 */
function buildSupportMessageIndicator(values: any[]) {
  let color: Color;
  let text: string;

  const role = values[1] as string;

  if (role === PROCUREMENT) {
    color = BADGE_COLORS.BLUE;
    text = SUPPORT_MESSAGES_LABELS.UP_TO_DATE;
  } else if (role) {
    color = BADGE_COLORS.RED;
    text = SUPPORT_MESSAGES_LABELS.PENDING;
  } else {
    color = BADGE_COLORS.GREY;
    text = SUPPORT_MESSAGES_LABELS.NONE;
  }

  return <Badge color={color}>{text}</Badge>;
}

const renderItem = (item: any) => (
  <span key={item.itemId}>
    <strong>{item.quantity}</strong> {item.itemInformation.name}
  </span>
);

function buildItemsRequested(value: any[] | undefined) {

  const emptyState = (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <span>-</span>
    </div>
  );

  return (
    <ExpandableList
      data={value || []}
      emptyState={emptyState}
      maxVisible={DEFAULT_VISIBLE_ITEMS_COUNT}
      showLessText={getString("manageOrders.table.expandableView.showLess")}
      showMoreText={getString("manageOrders.table.expandableView.showMore")}
      renderItem={renderItem}
    />
  );
}
